// Shared plain-language glossary — the ONE source for jargon definitions
// across DHA PRIME (AC-UX-2, mockup 18 "patterns and states").
//
// Every term on this list exposes a hover/tap definition wherever it is
// rendered; the web app wraps terms with
// `apps/web/src/components/primitives/GlossaryTerm.jsx`, which reads from
// this module. Do not fork per-page definitions — extend this list instead.

export const GLOSSARY = Object.freeze({
  LOE: {
    term: 'LOE',
    expansion: 'Line of Effort',
    definition:
      "Line of Effort — one of the Director's named priority areas. Every project and request is aligned to exactly one LOE.",
  },
  PIF: {
    term: 'PIF',
    expansion: 'Project Intake Form',
    definition: 'Project Intake Form — the structured request that starts the OWHA intake process.',
  },
  RFS: {
    term: 'RFS',
    expansion: 'Request for Support',
    definition:
      'Request for Support — the second way work enters OWHA, alongside the Project Intake Form.',
  },
  COA: {
    term: 'COA',
    expansion: 'Course of Action',
    definition: 'Course of Action — one of several compared options in a decision.',
  },
  CPI: {
    term: 'CPI',
    expansion: 'Cost Performance Index',
    definition:
      'Cost Performance Index — 1.0 means spending is on plan; below 0.9 needs attention.',
  },
  SPI: {
    term: 'SPI',
    expansion: 'Schedule Performance Index',
    definition:
      'Schedule Performance Index — 1.0 means the schedule is on plan; below 0.9 needs attention.',
  },
  EVM: {
    term: 'EVM',
    expansion: 'Earned Value Management',
    definition:
      'Earned Value Management — comparing the work actually completed against the money and time spent to get it.',
  },
  eSAAR: {
    term: 'eSAAR',
    expansion: 'electronic System Authorization Access Request',
    definition: 'The electronic access-request workflow that grants system roles.',
  },
  'POA&M': {
    term: 'POA&M',
    expansion: 'Plan of Action & Milestones',
    definition: 'Plan of Action & Milestones — the tracked task list of a RAPID.',
  },
  MOE: {
    term: 'MOE',
    expansion: 'Measure of Effectiveness',
    definition: 'Measure of Effectiveness — did the change achieve the intended outcome.',
  },
  MOP: {
    term: 'MOP',
    expansion: 'Measure of Performance',
    definition: 'Measure of Performance — was the work itself done right.',
  },
  IOC: {
    term: 'IOC',
    expansion: 'Initial Operating Capability',
    definition:
      'Initial Operating Capability — the first date a capability is usable in the field.',
  },
  SIPOC: {
    term: 'SIPOC',
    expansion: 'Suppliers, Inputs, Process, Outputs, Customers',
    definition:
      'Suppliers, Inputs, Process, Outputs, Customers — a one-page map of how a process works end to end.',
  },
  Placemat: {
    term: 'Placemat',
    definition: 'The one-page project summary developed after intake.',
  },
  RAPID: {
    term: 'RAPID',
    definition: 'A six-week rapid improvement event with a scored go/no-go entry gate.',
  },
  CUI: {
    term: 'CUI',
    expansion: 'Controlled Unclassified Information',
    definition: 'Controlled Unclassified Information — data handled under DoD marking rules.',
  },

  // --- SME-onboarding expansion (capability map + Glossary page) ---
  // `autoWrap: false` marks common English words that must NOT be wrapped into
  // arbitrary body copy — a tooltip there would be noise. All-caps acronyms are
  // always safe to auto-wrap and carry no flag.
  OWHA: {
    term: 'OWHA',
    expansion: 'Office of the Warfighter Health Advantage',
    definition:
      'Office of the Warfighter Health Advantage — the DHA organization whose intake and delivery work this system supports.',
  },
  'DHA PRIME': {
    term: 'DHA PRIME',
    expansion: 'DHA Program Resource and Intelligence Management Engine',
    definition:
      'DHA Program Resource and Intelligence Management Engine — the portfolio management system you are using right now.',
    autoWrap: false,
  },
  intake: {
    term: 'intake',
    definition:
      'The structured request that starts a new piece of work and sends it for scoring and routing.',
    autoWrap: false,
  },
  'gate review': {
    term: 'gate review',
    definition:
      'A scheduled decision checkpoint where leaders approve whether work may move to the next stage.',
  },
  'lifecycle stage': {
    term: 'lifecycle stage',
    definition: 'The phase a project is in as it moves from intake through delivery to closeout.',
  },
  lineage: {
    term: 'lineage',
    definition:
      'The recorded trail showing where a number came from and every source it passed through.',
    autoWrap: false,
  },
  provenance: {
    term: 'provenance',
    definition:
      'The origin and handling history of a piece of information, so its trustworthiness is clear.',
    autoWrap: false,
  },
  'data freshness': {
    term: 'data freshness',
    definition: 'How recently the underlying information was refreshed from its source system.',
  },
  UAT: {
    term: 'UAT',
    expansion: 'User Acceptance Testing',
    definition:
      'User Acceptance Testing — the stage where real users confirm a change works before it goes live.',
  },
  'RAPID pod': {
    term: 'RAPID pod',
    definition:
      'The small cross-functional team assigned to run a single six-week rapid improvement event.',
  },
  'governed run': {
    term: 'governed run',
    definition:
      'An artificial-intelligence task that executes only under approved rules with its steps recorded for review.',
  },
  guardrail: {
    term: 'guardrail',
    definition:
      'A built-in limit that keeps an automated or artificial-intelligence action inside approved boundaries.',
  },
  connector: {
    term: 'connector',
    definition: 'A managed link that pulls information from an outside source system into this platform.',
    autoWrap: false,
  },
  reconciliation: {
    term: 'reconciliation',
    definition: 'The check that confirms two sources agree and flags any differences for follow-up.',
    autoWrap: false,
  },
  ingestion: {
    term: 'ingestion',
    definition: 'The step that brings outside information in, checks it, and commits it into the platform.',
    autoWrap: false,
  },
  'daily brief': {
    term: 'daily brief',
    definition: 'The short once-a-day summary of what changed and what needs your attention.',
  },
  'mission thread': {
    term: 'mission thread',
    definition:
      'The end-to-end line connecting a mission goal to the work and outcomes that support it.',
  },
  'closeout package': {
    term: 'closeout package',
    definition: 'The final set of records that formally wraps up a completed project.',
  },
  'lessons learned': {
    term: 'lessons learned',
    definition:
      'Captured insights from finished work so future teams can repeat wins and avoid repeat mistakes.',
  },
  'status pack': {
    term: 'status pack',
    definition: 'A sanitized, read-only bundle of project status prepared for stakeholders.',
  },
  'briefing product': {
    term: 'briefing product',
    definition: 'A ready-to-share document generated from live records for a meeting or decision.',
  },
  'custom field': {
    term: 'custom field',
    definition:
      'An extra piece of information an administrator adds to a form to capture office-specific detail.',
  },
  'scoring rubric': {
    term: 'scoring rubric',
    definition: 'The weighted set of criteria used to score and rank incoming requests consistently.',
  },
  'saved view': {
    term: 'saved view',
    definition: 'A filtered arrangement of a page you keep so you can return to it in one click.',
  },
  'attention queue': {
    term: 'attention queue',
    definition: 'The prioritized list of items waiting for you to review or act on.',
  },
  persona: {
    term: 'persona',
    definition: 'A predefined role profile that determines what a person can see and do in the system.',
    autoWrap: false,
  },
  RBAC: {
    term: 'RBAC',
    expansion: 'Role-Based Access Control',
    definition:
      'Role-Based Access Control — the rule set that decides which screens and actions each role may use.',
  },
  'work item': {
    term: 'work item',
    definition: 'A single tracked unit of work such as a task, risk, or action assigned to someone.',
  },
  'division scope': {
    term: 'division scope',
    definition:
      'The set of offices and portfolios a person is allowed to see, based on their assignment.',
  },
  FMIS: {
    term: 'FMIS',
    expansion: 'Financial Management Information System',
    definition:
      'Financial Management Information System — the official system of record for appropriation and spending data.',
  },
  Advana: {
    term: 'Advana',
    definition:
      'The Department of Defense enterprise data platform that many source feeds are drawn from.',
  },
});

/** Case-insensitive lookup of a glossary entry ({term, expansion?, definition}). */
export function getGlossaryEntry(term) {
  if (!term) return null;
  if (GLOSSARY[term]) return GLOSSARY[term];
  const lower = String(term).toLowerCase();
  for (const entry of Object.values(GLOSSARY)) {
    if (entry.term.toLowerCase() === lower) return entry;
  }
  return null;
}

/** Case-insensitive definition lookup; empty string when the term is unknown. */
export function getGlossaryDefinition(term) {
  return getGlossaryEntry(term)?.definition || '';
}

/** Canonical term spellings, longest first (stable for tokenizers/markers). */
export function listGlossaryTerms() {
  return Object.values(GLOSSARY)
    .map((entry) => entry.term)
    .sort((a, b) => b.length - a.length || a.localeCompare(b));
}

/**
 * Terms that are SAFE to auto-wrap into arbitrary body copy, longest first.
 * Excludes entries flagged `autoWrap: false` — common English words (e.g.
 * "intake", "persona") whose tooltip would be noise in ordinary prose. The
 * <GlossaryTerm> component and the Glossary page still resolve every term via
 * listGlossaryTerms()/getGlossaryEntry(); only the auto-wrapper is narrowed.
 */
export function listAutoWrapTerms() {
  return Object.values(GLOSSARY)
    .filter((entry) => entry.autoWrap !== false)
    .map((entry) => entry.term)
    .sort((a, b) => b.length - a.length || a.localeCompare(b));
}
